import { useEffect, useRef, useState } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import { sluggify } from "../../utils/misc";
import { cn } from "../../lib/utils";
import { Progress } from "./progress";
import { Button } from "./button";
import { Trash } from "lucide-react";

type DropZonePropsType = {
    message?: string;
    maxFiles: number;
    maxSize: number;
    accept: Record<string, string[]>;
    bucketName: string;
    onUploaded: (data: string) => void;
    onRemoved?: (url: string) => void;
    onUploadingChange?: (uploading: boolean) => void;
    folderPath?: string;
    required?: boolean;
    fileName?: string;
};

const DropZone = ({
    fileName,
    message = "Déposez votre fichier ou cliquez ici",
    maxFiles,
    maxSize,
    accept,
    bucketName,
    onUploaded,
    onRemoved,
    onUploadingChange,
    folderPath,
    required = false
}: DropZonePropsType) => {
    const errorMapping: Record<string, string> = {
        "file-invalid-type": "Fichier non valide",
        "file-too-large": "Fichier trop volumineux",
        "file-too-small": "Fichier trop petit",
        "too-many-files": "Fichiers trop nombreux"
    };

    const { isFocused, isDragAccept, isDragReject, acceptedFiles, fileRejections, getRootProps, getInputProps } = useDropzone({
        maxFiles: maxFiles,
        maxSize: maxSize * 1e6,
        accept: accept
    });

    const [dropedFiles, setDropedFiles] = useState<File[]>([]);
    const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
    const [progress, setProgress] = useState<Record<string, number>>({});
    const [uploadingCount, setUploadingCount] = useState(0);
    const cancelledFilesRef = useRef(new Set<string>());
    const startedUploadsRef = useRef(new Set<string>());

    const buildFileUrl = (file: File) => {
        const s3FileName = fileName ? sluggify(fileName) : sluggify(file.name);
        const filePath = folderPath ? `${folderPath}/${s3FileName}` : s3FileName;
        return `https://${bucketName}.s3.eu-west-3.amazonaws.com/${filePath}`;
    };

    useEffect(() => {
        setDropedFiles(prevState => {
            if (maxFiles <= prevState.length) return prevState;
            // acceptedFiles => add to dropped files if not already there AND not in excess of maxFiles
            const filteredFiles = acceptedFiles.filter(file => !dropedFiles.map(f => f.name).includes(file.name));
            return [...prevState, ...filteredFiles];
        });
    }, [acceptedFiles]);

    useEffect(() => {
        setRejectedFiles(prevState => {
            const filteredFiles = fileRejections.filter(fileError => !rejectedFiles.map(f => f.file.name).includes(fileError.file.name));
            return prevState?.concat(filteredFiles);
        });
    }, [fileRejections]);

    useEffect(() => {
        dropedFiles.map(file => {
            uploadFile(file);
        });
    }, [dropedFiles]);

    useEffect(() => {
        onUploadingChange?.(uploadingCount > 0);
    }, [uploadingCount, onUploadingChange]);

    const removeFile = (name: string) => {
        cancelledFilesRef.current.add(name);
        const file = dropedFiles.find(f => f.name === name);
        if (file) {
            onRemoved?.(buildFileUrl(file));
        }
        if (startedUploadsRef.current.has(name)) {
            startedUploadsRef.current.delete(name);
            setUploadingCount(count => Math.max(0, count - 1));
        }
        setDropedFiles(prevState => {
            return prevState.filter(f => f.name !== name);
        });
        setRejectedFiles(prevState => {
            return prevState.filter(f => f.file.name !== name);
        });
    };

    const uploadFileWithXHR = (file: File, bucketName: string, filePath: string) => {
        if (startedUploadsRef.current.has(file.name)) {
            return;
        }
        startedUploadsRef.current.add(file.name);
        setUploadingCount(count => count + 1);

        const xhr = new XMLHttpRequest();
        const url = `https://${bucketName}.s3.eu-west-3.amazonaws.com/${filePath}`;

        xhr.open("PUT", url, true);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.setRequestHeader("x-amz-acl", "public-read");

        xhr.upload.onprogress = evt => {
            if (evt.lengthComputable) {
                const percent = Math.round((evt.loaded / evt.total) * 100);
                setProgress(prevState => ({
                    ...prevState,
                    [file.name]: percent
                }));
            }
        };

        const finishUpload = () => {
            if (startedUploadsRef.current.has(file.name)) {
                startedUploadsRef.current.delete(file.name);
                setUploadingCount(count => Math.max(0, count - 1));
            }
        };

        xhr.onload = () => {
            finishUpload();
            if (cancelledFilesRef.current.has(file.name)) {
                return;
            }
            if (xhr.status === 200) {
                const fileUrl = `https://${bucketName}.s3.eu-west-3.amazonaws.com/${filePath}`;
                onUploaded(fileUrl);
            } else {
                console.error("Upload failed:", xhr.status, xhr.statusText);
            }
        };

        xhr.onerror = () => {
            finishUpload();
            console.error("XHR error:", xhr.statusText);
        };

        xhr.send(file);
    };

    const uploadFile = (file: File) => {
        if (cancelledFilesRef.current.has(file.name)) {
            return;
        }
        const s3FileName = fileName ? sluggify(fileName) : sluggify(file.name);
        const filePath = folderPath ? `${folderPath}/${s3FileName}` : s3FileName;
        uploadFileWithXHR(file, bucketName, filePath);
    };

    return (
        <div
            id="dropZone"
            className={cn(
                "DropZone flex h-full cursor-pointer flex-col items-center justify-center rounded border-[1px] p-5",
                isDragAccept ? "border-green-500" : "",
                isDragReject ? "border-red-500" : "",
                isFocused ? "border-blue-500" : ""
            )}
            {...getRootProps()}
        >
            <input {...getInputProps()} />
            {dropedFiles.length === 0 && rejectedFiles.length === 0 && (
                <div className="flex flex-col items-center justify-center">
                    <p className="text-base">
                        {message}
                        {required ? " *" : ""}
                    </p>
                    <p className="text-xs">{Object.values(accept).flat().join(", ")}</p>
                </div>
            )}
            {rejectedFiles.length !== 0 && (
                <div className="flex flex-col items-center justify-center">
                    {rejectedFiles.map(rejection => {
                        return (
                            <div key={rejection.file.name} className="relative m-1 flex items-center space-x-2 rounded-md bg-red-100 p-2">
                                <div className="flex flex-col">
                                    <p>{rejection.file.name}</p>
                                    {rejection.errors.map(error => {
                                        return (
                                            <p key={error.code} className="text-xs text-red-500">
                                                {errorMapping[error.code] || "Erreur fichier"}
                                            </p>
                                        );
                                    })}
                                </div>
                                <Button
                                    size="icon"
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        removeFile(rejection.file.name);
                                    }}
                                >
                                    <Trash />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}

            {dropedFiles.length !== 0 && (
                <div className="flex flex-col items-center justify-center">
                    {dropedFiles.map(file => {
                        return (
                            <div key={file.name} className="relative m-1 flex items-center space-x-2 rounded-md bg-slate-100 p-2">
                                <div className="flex flex-col">
                                    <p>{file.name}</p>
                                    <Progress value={progress[file.name] || 0} />
                                </div>

                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        removeFile(file.name);
                                    }}
                                >
                                    <Trash />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DropZone;
